import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import HomeSharpIcon from '@mui/icons-material/HomeSharp';
import { Settings } from '@mui/icons-material';
import { Avatar, Stack, Toolbar, Tooltip, useMediaQuery } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import IconButton from '@mui/material/IconButton';

import { logoutUser } from 'redux/slices/user/userSlice';
import AppBarComponent from 'components/Common/AppBar';
import { AppDispatch, RootState } from 'redux/store';
import { notify } from './Toast';
import MuiLoader from './Loader';
import routes from 'constants/routes';
import Colors from 'constants/colors';
import { SEND_MONEY } from 'types/sendAndRequest';
import GroupsIcon from '@mui/icons-material/Groups';
import WalletIcon from 'assets/wallet.png';
import { setSendAndRequestTab } from 'redux/slices/common/appSlice';
import { isMerchant } from 'utils/helpers';
interface AppBarComponentProps {
    children: React.ReactElement;
}
interface MenuItem {
    title: string;
    icon: React.ReactElement | string;
    navigation: string;
    isVisible: boolean;
    setCurrentTab?: (dispatch: AppDispatch) => void;
}

const drawerWidthInVh = 38; // Adjust the value as needed

const drawerWidth = `${drawerWidthInVh}vh`;

const { account, staff, dashboard, sendAndRequest, sendmoney } = routes;

const excludedPaths = [
    account.main,
    staff.main,
    dashboard.main,
    sendAndRequest.main,
    sendAndRequest.sendMoneyForm,
    account.changePassword,
    sendmoney.main,
];

export default function DrawerLeft({ children }: AppBarComponentProps) {
    const theme = useTheme();
    let navigation = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    let location = useLocation();
    const [open, setOpen] = React.useState(true);
    const [isLoading, setIsLoading] = React.useState(false);
    const userInfo = useSelector((state: RootState) => state.user?.userInfo);
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const { businessName, merchantImgUrl, nickname } = userInfo || {};
    const menuItems: MenuItem[] = [
        {
            title: 'Dashboard',
            icon: <HomeSharpIcon />,
            navigation: dashboard.main,
            isVisible: true,
        },

        {
            title: 'Send & Request',
            icon: WalletIcon,
            navigation: sendAndRequest.main,
            isVisible: true,
            setCurrentTab: setSendAndRequestTab(SEND_MONEY),
        },
        {
            title: 'Staff',
            icon: <GroupsIcon />,
            navigation: staff.main,
            isVisible: !!isMerchant(),
        },
        {
            title: 'My Account',
            icon: <Settings />,
            navigation: account.main,
            isVisible: true,
        },
    ];
    React.useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    const handelLogOutUser = async () => {
        setIsLoading(true);
        try {
            await dispatch(logoutUser());
            navigation(routes.auth.login);
        } catch (err) {
            notify('Something went wrong!', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleMenuItemClick = (item: MenuItem) => {
        const { setCurrentTab } = item || {};
        setCurrentTab && dispatch(setCurrentTab);
        return item.navigation;
    };

    const drawer = (
        <Box sx={{ height: '100%', justifyContent: 'space-between', display: 'flex', flexDirection: 'column' }}>
            <Box>
                <Toolbar />
                <Box display="flex" flexDirection="column" alignItems="center" width="100%">
                    <Avatar sx={{ width: 140, height: 140 }} src={merchantImgUrl} alt="User Avatar" />
                    <Tooltip title={nickname}>
                        <Typography
                            variant={'h6'}
                            style={{
                                marginTop: theme.spacing(1),
                                fontSize: 20,
                                textAlign: 'center',
                            }}
                            textOverflow={'ellipsis'}
                            width={'90%'}
                            overflow={'hidden'}
                            whiteSpace={'nowrap'}
                        >
                            {nickname}
                        </Typography>
                    </Tooltip>
                    <Tooltip title={businessName}>
                        <Typography
                            variant={'h6'}
                            style={{
                                marginTop: theme.spacing(1),
                                color: '#6B7280',
                                fontSize: 16,
                                textAlign: 'center',
                            }}
                            textOverflow={'ellipsis'}
                            width={'90%'}
                            overflow={'hidden'}
                            whiteSpace={'nowrap'}
                        >
                            {businessName}
                        </Typography>
                    </Tooltip>
                </Box>
                <Divider sx={{ marginBottom: 2 }} />

                <Stack>
                    {menuItems.map((item, index) => {
                        if (!item.isVisible) return null;
                        return (
                            <Link key={index} to={item.navigation} style={{ textDecoration: 'none' }}>
                                <ListItem
                                    key={item.title}
                                    disablePadding
                                    className={location.pathname === item.navigation ? 'selected' : ''}
                                    onClick={() => handleMenuItemClick(item)}
                                >
                                    <ListItemButton>
                                        {typeof item.icon === 'string' ? (
                                            <Avatar sx={{ width: 24, height: 24 }} src={item.icon} alt={item.title} />
                                        ) : (
                                            item.icon
                                        )}
                                        <ListItemText style={{ marginLeft: 16, color: Colors.darkTextColor }} primary={item.title} />
                                    </ListItemButton>
                                </ListItem>
                            </Link>
                        );
                    })}
                </Stack>
            </Box>
            <Box>
                <Box style={{ marginTop: 'auto' }}>
                    <Divider />
                    <Stack
                        onClick={handelLogOutUser}
                        direction="row"
                        alignItems="center"
                        sx={{ '&:hover': { backgroundColor: 'rgb(232,232,232, 1)' }, cursor: 'pointer' }}
                    >
                        <IconButton disabled={isLoading}>
                            <LogoutIcon />
                        </IconButton>
                        <Typography sx={{ marginLeft: 1 }} variant="body2">
                            Logout
                        </Typography>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );

    return (
        <>
            <MuiLoader absolute loading={isLoading} />
            <AppBarComponent
                hideMenuIcon={isDesktop}
                handleDrawerClose={handleDrawerClose}
                open={open}
                handleDrawerOpen={handleDrawerToggle}
            />

            <Box sx={{ display: 'flex' }}>
                <CssBaseline />

                <Box component="nav" sx={{ flexShrink: { sm: 0 } }} aria-label="mailbox folders">
                    <Drawer
                        variant={isDesktop ? 'permanent' : 'temporary'}
                        open={open}
                        onClose={handleDrawerClose}
                        ModalProps={{
                            keepMounted: true,
                        }}
                        sx={{
                            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                        }}
                    >
                        <Toolbar />
                        {drawer}
                    </Drawer>
                </Box>

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        p: 3,
                        width: { sm: `calc(100% - ${drawerWidth})` },
                        marginLeft: { lg: open || isDesktop ? `${drawerWidth}` : 0 },
                        backgroundColor: '#F2F2F6',
                        minHeight: '100vh',
                    }}
                >
                    <Toolbar />
                    {!open && !excludedPaths.includes(location.pathname) && (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                marginLeft: 2,
                            }}
                        >
                            {/* <Box> */}
                            {/* <Stack direction='row' alignItems='center' >
                <IconButton onClick={handleNavigation}>
                  <KeyboardBackspaceIcon />
                </IconButton>
                <Typography>Back</Typography>
              </Stack> */}
                        </Box>
                    )}

                    {children}
                </Box>
            </Box>
        </>
    );
}
